/**
 * Everything this app remembers in the browser lives in ONE localStorage entry,
 * a JSON object under the project name. Nothing else should call localStorage
 * directly — add a field to StoredState instead.
 *
 * Reads always go back to localStorage (no in-memory copy) so a write from one
 * tab never clobbers what another tab saved in the meantime.
 */

export const STORAGE_KEY = 'magister-streaming'

export interface StoredRoom {
  /** This browser created the room through the web form (see session.service). */
  owner?: boolean
  /** Cached display name. */
  name?: string
  /** Last chat messages, restored on reload. */
  chat?: unknown[]
  /**
   * Join preferences carried from JoinRoomPage to RoomPage (see session.service).
   * Per room so several rooms can be open at once in tabs of the same browser.
   */
  session?: unknown
}

export interface StoredState {
  /** Old single join handoff for every room; folded into rooms[roomId].session. */
  session?: unknown
  uiLanguage?: string
  captionLanguage?: string
  /** El idioma en que dice hablar (el «Hablo en» del menú de subtítulos). */
  captionVoice?: string
  captionSize?: string
  recordingSubtitles?: boolean
  filterQuality?: string
  audioAutoGain?: boolean
  cameraRounded?: boolean
  /** Chat sin sonido ni avisos emergentes (el contador de no leídos sigue). */
  chatMuted?: boolean
  rooms?: Record<string, StoredRoom>
}

function load(): StoredState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

function save(state: StoredState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    /* quota / storage blocked — not remembered, still applies now */
  }
}

export function readStore<K extends keyof StoredState>(key: K): StoredState[K] | undefined {
  return load()[key]
}

/** Set one field; `undefined` removes it. */
export function writeStore<K extends keyof StoredState>(key: K, value: StoredState[K] | undefined): void {
  const state = load()
  if (value === undefined) delete state[key]
  else state[key] = value
  save(state)
}

export function readRoom(roomId: string): StoredRoom {
  return (roomId && load().rooms?.[roomId]) || {}
}

export function writeRoom(roomId: string, patch: Partial<StoredRoom>): void {
  if (!roomId) return
  const state = load()
  const rooms = state.rooms ?? {}
  rooms[roomId] = { ...rooms[roomId], ...patch }
  state.rooms = rooms
  save(state)
}

// ─── Migration from the old scattered keys ─────────────────────────────────
// Folds whatever an older build left behind into the single entry and deletes
// the old keys. Values already present in the new entry win.

const bool = (v: string) => v !== '0'
const LEGACY_KEYS: Record<string, [keyof StoredState, (v: string) => unknown]> = {
  videoStreamingSession: ['session', (v) => JSON.parse(v)],
  meetsUiLanguage: ['uiLanguage', String],
  meetsCaptionLanguage: ['captionLanguage', String],
  meetsCaptionVoice: ['captionVoice', String],
  meetsCaptionSize: ['captionSize', String],
  recordingSubtitlesEnabled: ['recordingSubtitles', bool],
  'video-filter-quality': ['filterQuality', String],
  'audio-auto-gain': ['audioAutoGain', (v) => v === '1'],
  cameraRounded: ['cameraRounded', bool],
}
const LEGACY_ROOM_PREFIXES: Record<string, [keyof StoredRoom, (v: string) => unknown]> = {
  'magister:roomOwner:': ['owner', (v) => v === '1'],
  'magister:roomName:': ['name', String],
  'magister:chat:': ['chat', (v) => JSON.parse(v)],
}

function migrateLegacyKeys(): void {
  let ls: Storage
  try {
    ls = window.localStorage
  } catch {
    return
  }
  const keys: string[] = []
  for (let i = 0; i < ls.length; i++) {
    const k = ls.key(i)
    if (k && k !== STORAGE_KEY) keys.push(k)
  }
  const state = load()
  let changed = false
  for (const k of keys) {
    const raw = ls.getItem(k)
    if (raw === null) continue
    const direct = LEGACY_KEYS[k]
    const prefix = direct ? undefined : Object.keys(LEGACY_ROOM_PREFIXES).find((p) => k.startsWith(p))
    if (!direct && !prefix) continue
    try {
      if (direct) {
        const [field, parse] = direct
        if (state[field] === undefined) (state as Record<string, unknown>)[field] = parse(raw)
      } else if (prefix) {
        const [field, parse] = LEGACY_ROOM_PREFIXES[prefix]
        const roomId = k.slice(prefix.length)
        const rooms = (state.rooms ??= {})
        const room = (rooms[roomId] ??= {})
        if (room[field] === undefined) (room as Record<string, unknown>)[field] = parse(raw)
      }
    } catch {
      /* unreadable old value — just drop it */
    }
    try { ls.removeItem(k) } catch { /* noop */ }
    changed = true
  }
  // The join handoff used to be one global field, so joining a room in one tab
  // overwrote the room open in another (see session.service).
  const legacy = state.session as { roomId?: unknown } | undefined
  if (legacy !== undefined) {
    if (legacy && typeof legacy === 'object' && typeof legacy.roomId === 'string' && legacy.roomId) {
      const rooms = (state.rooms ??= {})
      const room = (rooms[legacy.roomId] ??= {})
      if (room.session === undefined) room.session = legacy
    }
    delete state.session
    changed = true
  }
  if (changed) save(state)
}

migrateLegacyKeys()
